/**
 * JSON → TSV Tool
 */

import * as Papa from 'papaparse';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';
import { parseJsonLenient } from '@/lib/jsonParser';

import { computeGlobalColumnOrderFromObjects } from './columnOrder';
import { normalizeJsonText } from './json';

export const JsonToTsvTool: ITransformerTool = {
  id: 'json-to-tsv',
  name: 'JSON → TSV',
  description: 'Convert JSON (array of objects or arrays) to TSV',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['json', 'tsv', 'convert', 'table', 'tabular'],

  supportedInputTypes: [SEMANTIC_TYPES.JSON],
  outputType: SEMANTIC_TYPES.TSV,

  attributes: [
    {
      name: 'lineEnding',
      type: 'select',
      label: 'Line Ending',
      defaultValue: 'LF',
      options: ['LF', 'CRLF'],
      description: 'Newline style (CRLF can paste into Excel more reliably on Windows)',
    },
    {
      name: 'includeHeader',
      type: 'boolean',
      label: 'Include Header',
      defaultValue: true,
      description: 'Include a header row when converting objects',
    },
    {
      name: 'preserveColumnOrder',
      type: 'boolean',
      label: 'Preserve Column Order',
      defaultValue: true,
      description: 'Preserve the key order from the source JSON (instead of sorting columns)',
    },
    {
      name: 'quoteAll',
      type: 'boolean',
      label: 'Quote All Cells',
      defaultValue: true,
      description: 'Always wrap cell values in quotes',
    },
  ],

  supportsInput(input: string, detectedType?: string): boolean {
    if (detectedType !== 'JSON') return false;
    try {
      parseJsonLenient(normalizeJsonText(input));
      return true;
    } catch {
      return false;
    }
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    let parsed: unknown;
    try {
      parsed = parseJsonLenient(normalizeJsonText(input));
    } catch (e) {
      const error = e as Error;
      throw ToolError.validation(`Invalid JSON: ${error.message}`, {
        suggestion: 'Check for missing commas, quotes, or brackets',
      });
    }

    const includeHeader = config.includeHeader !== false;
    const preserveColumnOrder = config.preserveColumnOrder !== false;
    const quoteAll = config.quoteAll !== false;
    const newline = config.lineEnding === 'CRLF' ? '\r\n' : '\n';

    // Accept either:
    // - array of objects/arrays (primary use-case)
    // - single object (common "valid JSON" input) -> treated as single-row
    const normalizedArray: unknown[] = Array.isArray(parsed)
      ? parsed
      : parsed !== null && typeof parsed === 'object'
        ? [parsed]
        : [];

    if (normalizedArray.length === 0) {
      if (Array.isArray(parsed)) return '';
      throw ToolError.validation('Invalid JSON: expected a JSON array or object', {
        suggestion: 'Provide an object, an array of objects, or an array of arrays',
      });
    }

    const first = normalizedArray[0];

    // Array of arrays
    if (Array.isArray(first)) {
      const rows = normalizedArray.map((r) => {
        if (!Array.isArray(r)) {
          throw ToolError.validation('Invalid JSON: mixed array types', {
            suggestion: 'All items must be arrays when using array-of-arrays conversion',
          });
        }
        return r.map((c) => (c === null || c === undefined ? null : c));
      });

      return Papa.unparse(rows, {
        delimiter: '\t',
        newline,
        quotes: quoteAll,
      });
    }

    // Array of objects
    if (first !== null && typeof first === 'object') {
      const objects = normalizedArray as Array<Record<string, unknown>>;

      const headerKeys = preserveColumnOrder
        ? computeGlobalColumnOrderFromObjects(objects)
        : Array.from(
            objects.reduce((set, obj) => {
              for (const k of Object.keys(obj ?? {})) set.add(k);
              return set;
            }, new Set<string>())
          ).sort();

      const rows: Array<Array<string | number | boolean | null>> = [];

      if (includeHeader) {
        rows.push(headerKeys);
      }

      for (const obj of objects) {
        rows.push(
          headerKeys.map((k) => {
            const v = obj?.[k];
            if (v === null || v === undefined) return null;
            if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
            return JSON.stringify(v);
          })
        );
      }

      return Papa.unparse(rows, {
        delimiter: '\t',
        newline,
        quotes: quoteAll,
      });
    }

    throw ToolError.validation('Invalid JSON: expected array of objects or arrays', {
      suggestion: 'Provide an array of objects or an array of arrays',
    });
  },
};
